'use client';

import { useMicrocopy } from '@lib/cms/microcopy/client';
import { useCountdown } from '@lib/hooks/useCountdown';
import c from 'classnames';
import { formatCountdown } from 'ui-components';
import { interpolateStringWithData } from 'utils';
import { Sale } from './types';

type ProductCardSaleCountdownProps = {
  sale: Sale;
  isActive?: boolean;
};

/**
 * Renders the sale countdown as an inline pill in the product card price row
 * (red for an active sale, green "Sale starts …" for an upcoming flash deal),
 * matching the price component in Figma. Returns null until the countdown is
 * ready; the pill also suppresses hydration warnings since the time value
 * differs between the server render and the first client render.
 */
export const ProductCardSaleCountdown = ({
  sale,
  isActive,
}: ProductCardSaleCountdownProps) => {
  // Known limitation (accepted for this experiment): these treatment defaults
  // share the `product-card` microcopy keys with the control overlay, so a
  // populated CMS value overrides the treatment wording/format, and the new
  // `countdown-minutes-only` key falls back to English until the CMS set is
  // updated. Revisit with treatment-specific keys if the redesign ships.
  const microcopy = useMicrocopy('product-card', {
    'product-card.sale-starts': 'Sale starts {{time}}',
    'product-card.countdown-with-days': '{{days}}d {{hours}}h',
    'product-card.countdown-hours-only': '{{hours}}h {{minutes}}m',
    'product-card.countdown-minutes-only': '{{minutes}}m {{seconds}}s',
  });

  const countdownDate = isActive ? sale.dates.ends : sale.dates.starts;
  const countdownDuration = useCountdown(countdownDate);

  if (!countdownDuration || !countdownDate) {
    return null;
  }

  const countdownFormatted = formatCountdown(countdownDate, countdownDuration, {
    withDays: microcopy['product-card.countdown-with-days'],
    hoursOnly: microcopy['product-card.countdown-hours-only'],
    minutesOnly: microcopy['product-card.countdown-minutes-only'],
  });

  return (
    <span
      className={c('rounded px-1 text-xs leading-5', {
        'bg-red-200 text-red-600': isActive,
        'bg-green-300 text-green-600': !isActive,
      })}
      data-test="product-card-flash-deal-info"
      suppressHydrationWarning
    >
      <span
        data-test={
          isActive ? 'active-flash-deal-stage' : 'upcoming-flash-deal-stage'
        }
      >
        {isActive
          ? countdownFormatted
          : interpolateStringWithData(microcopy['product-card.sale-starts'], {
              time: countdownFormatted,
            })}
      </span>
    </span>
  );
};
